import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Flame, Wind, Atom, CloudFog, Zap, Package, Wrench, Gauge, Settings,
  HeartPulse, Shield, Truck, ArrowLeft, Search, ArrowRight, X, BadgeCheck,
  Clock, Tag,
} from "lucide-react";
import Navbar from "@/components/cima/Navbar";
import Footer from "@/components/cima/Footer";
import WhatsAppFloat from "@/components/cima/WhatsAppFloat";
import QuoteDialog from "@/components/cima/QuoteDialog";
import InfraLogo from "@/components/cima/InfraLogo";
import { CATEGORIES, MEDICAL_SUBCATEGORIES, PRODUCTS } from "@/data/catalog";
import { CIMA } from "@/lib/cima";

const ICONS = {
  Flame, Wind, Atom, CloudFog, Zap, Package, Wrench, Gauge, Settings,
  HeartPulse, Shield, Truck,
};

const ACCENT_BG = {
  red: "bg-gradient-to-br from-[#D90429] via-[#B0001E] to-[#0F172A]",
  orange: "bg-gradient-to-br from-[#EA580C] via-[#C2410C] to-[#0F172A]",
  navy: "bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]",
};

const MED_ICON_COLOR = {
  "med-navy": "text-[#003DA5]",
  "med-red": "text-[var(--cima-red)]",
};

// Normalize text for accent-insensitive search:
// "Oxígeno" -> "oxigeno", "Hidrógeno" -> "hidrogeno", "CO₂" -> "co2"
const norm = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/₀/g, "0").replace(/₁/g, "1").replace(/₂/g, "2").replace(/₃/g, "3")
    .replace(/₄/g, "4").replace(/₅/g, "5").replace(/₆/g, "6").replace(/₇/g, "7")
    .replace(/₈/g, "8").replace(/₉/g, "9")
    .toLowerCase();

export default function Catalog() {
  const location = useLocation();
  const [activeCat, setActiveCat] = useState("all");
  const [activeSubCat, setActiveSubCat] = useState("all");
  const [search, setSearch] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestIndex, setSuggestIndex] = useState(-1);
  const [recentIds, setRecentIds] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState("");
  const searchBoxRef = useRef(null);

  // Load recently viewed from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("cima_recent_v1");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setRecentIds(arr.filter((x) => typeof x === "string").slice(0, 8));
      }
    } catch (e) { /* ignore */ }
  }, []);

  const addToRecent = (productId) => {
    if (!productId) return;
    setRecentIds((prev) => {
      const next = [productId, ...prev.filter((id) => id !== productId)].slice(0, 8);
      try { localStorage.setItem("cima_recent_v1", JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const clearRecent = () => {
    setRecentIds([]);
    try { localStorage.removeItem("cima_recent_v1"); } catch (e) { /* ignore */ }
  };

  const recentProducts = useMemo(
    () => recentIds.map((id) => PRODUCTS.find((p) => p.id === id)).filter(Boolean),
    [recentIds]
  );

  const openQuote = (productName = "", productId = "") => {
    setQuoteProduct(productName);
    setQuoteOpen(true);
    if (productId) addToRecent(productId);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (hash && CATEGORIES.some((c) => c.id === hash)) {
      setActiveCat(hash);
      setActiveSubCat("all");
      setTimeout(() => {
        document.getElementById("catalog-results")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 200);
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const filtered = useMemo(() => {
    const term = norm(search.trim());
    return PRODUCTS.filter((p) => {
      const matchCat = activeCat === "all" || p.category === activeCat;
      const matchSub =
        activeCat !== "medicinal" || activeSubCat === "all" || p.subcategory === activeSubCat;
      const matchTerm =
        !term ||
        norm(p.name).includes(term) ||
        norm(p.desc).includes(term) ||
        (p.tag && norm(p.tag).includes(term)) ||
        (p.sizes && p.sizes.some((s) => norm(s).includes(term)));
      return matchCat && matchSub && matchTerm;
    });
  }, [activeCat, activeSubCat, search]);

  // Smart autocomplete suggestions: top products + matching tags + matching categories
  const suggestions = useMemo(() => {
    const term = norm(search.trim());
    if (term.length < 1) return { products: [], tags: [], categories: [] };

    const products = PRODUCTS
      .filter((p) =>
        norm(p.name).includes(term) ||
        (p.tag && norm(p.tag).includes(term)) ||
        (p.sizes && p.sizes.some((s) => norm(s).includes(term)))
      )
      .slice(0, 6);

    const tagSet = new Set();
    PRODUCTS.forEach((p) => {
      if (p.tag && norm(p.tag).includes(term)) tagSet.add(p.tag);
      if (p.sizes) p.sizes.forEach((s) => { if (norm(s).includes(term)) tagSet.add(s); });
    });
    const tags = Array.from(tagSet).slice(0, 4);

    const categories = CATEGORIES
      .filter((c) =>
        norm(c.name).includes(term) ||
        norm(c.subtitle).includes(term) ||
        norm(c.description).includes(term)
      )
      .slice(0, 3);

    return { products, tags, categories };
  }, [search]);

  const totalSuggestions =
    suggestions.products.length + suggestions.tags.length + suggestions.categories.length;

  const selectSuggestion = (kind, value) => {
    setSuggestOpen(false);
    setSuggestIndex(-1);
    if (kind === "product") {
      setSearch(value.name);
      setActiveCat("all");
      setActiveSubCat("all");
      addToRecent(value.id);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="catalog-product-${value.id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        else document.getElementById("catalog-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else if (kind === "tag") {
      setSearch(value);
      setActiveCat("all");
      setTimeout(() => {
        document.getElementById("catalog-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else if (kind === "category") {
      setSearch("");
      handleCatClick(value.id);
    }
  };

  const onSearchKeyDown = (e) => {
    if (!suggestOpen || totalSuggestions === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestIndex((i) => (i + 1) % totalSuggestions);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestIndex((i) => (i - 1 + totalSuggestions) % totalSuggestions);
    } else if (e.key === "Enter" && suggestIndex >= 0) {
      e.preventDefault();
      // Map flat index to suggestion type
      let idx = suggestIndex;
      if (idx < suggestions.products.length) {
        selectSuggestion("product", suggestions.products[idx]);
        return;
      }
      idx -= suggestions.products.length;
      if (idx < suggestions.tags.length) {
        selectSuggestion("tag", suggestions.tags[idx]);
        return;
      }
      idx -= suggestions.tags.length;
      if (idx < suggestions.categories.length) {
        selectSuggestion("category", suggestions.categories[idx]);
      }
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
      setSuggestIndex(-1);
    }
  };

  const handleCatClick = (id) => {
    setActiveCat(id);
    setActiveSubCat("all");
    setTimeout(() => {
      document.getElementById("catalog-results")?.scrollIntoView({
        behavior: "smooth", block: "start",
      });
    }, 50);
  };

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-white">
      <Navbar onOpenQuote={() => openQuote("")} />

      {/* HERO */}
      <section
        data-testid="catalog-hero"
        className="relative pt-28 lg:pt-36 pb-12 lg:pb-16 bg-[var(--cima-navy)] text-white overflow-hidden border-b border-white/10"
      >
        <div
          className="absolute inset-0 -z-10 opacity-50"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(217,4,41,0.22) 0%, rgba(15,23,42,0) 65%)",
          }}
        />
        <div
          className="absolute inset-0 -z-10 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="absolute -bottom-10 -right-10 w-44 h-44 stripe-accent opacity-25 rotate-12" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="catalog-back-link"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white font-mono-cima uppercase tracking-widest mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-12 bg-[var(--cima-orange)]" />
                <span className="font-mono-cima text-[10px] uppercase tracking-[0.35em] text-white/70">
                  Catálogo CIMA · Zihuatanejo
                </span>
              </div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Todo lo que necesitas{" "}
                <span className="text-[var(--cima-red)]">en un solo lugar</span>.
              </h1>
              <p className="mt-5 text-white/80 max-w-2xl">
                Gases industriales, soldadura, consumibles y soluciones medicinales —
                con la calidad, seguridad y atención profesional que CIMA te ofrece
                desde 1972.
              </p>
              <div className="mt-6 inline-flex items-center gap-3 bg-white px-3 py-2">
                <BadgeCheck className="w-4 h-4 text-[#E30613]" />
                <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-700">
                  Distribuidor autorizado
                </span>
                <span className="border-l border-slate-200 pl-3">
                  <InfraLogo height={22} />
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white p-3 inline-flex">
                <img src={CIMA.logoUrl} alt="CIMA" className="h-16 lg:h-20 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section data-testid="catalog-categories" className="py-14 lg:py-16 bg-[var(--cima-surface)] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <div>
              <span className="divider-orange mb-4" />
              <p className="font-mono-cima text-xs uppercase tracking-widest text-[var(--cima-red)] mb-2">
                Categorías
              </p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[var(--cima-navy)]">
                Explora por categoría
              </h2>
            </div>
            <button
              type="button"
              data-testid="cat-show-all"
              onClick={() => handleCatClick("all")}
              className="btn-cima-ghost px-5 py-3 text-xs font-mono-cima uppercase tracking-widest"
            >
              Ver todos los productos
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {CATEGORIES.map((c) => {
              const Icon = ICONS[c.icon] || Package;
              const active = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`catalog-category-${c.id}`}
                  onClick={() => handleCatClick(c.id)}
                  className={`sharp-card group text-left overflow-hidden flex flex-col bg-white ${
                    active ? "border-[var(--cima-red)]" : ""
                  }`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-white border-b border-slate-100 flex items-center justify-center">
                    {c.image && (
                      <img
                        src={c.image}
                        alt={c.name}
                        loading="lazy"
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute top-3 left-3 bg-[var(--cima-red)] text-white px-2 py-1 text-[10px] font-mono-cima uppercase tracking-widest font-semibold">
                      Categoría
                    </div>
                    <div className="absolute top-3 right-3 w-9 h-9 bg-[var(--cima-navy)] text-white flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="p-4 bg-white">
                    <div className="font-mono-cima text-[10px] uppercase tracking-widest text-[var(--cima-red)] mb-1">
                      {c.subtitle}
                    </div>
                    <div className="font-display font-bold text-xl text-[var(--cima-navy)] leading-tight mb-2">
                      {c.name}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-slate-600 line-clamp-2 flex-1">{c.description}</p>
                      <ArrowRight className={`w-4 h-4 shrink-0 ${active ? "text-[var(--cima-red)]" : "text-slate-400 group-hover:text-[var(--cima-red)]"} transition-colors`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section id="catalog-results" data-testid="catalog-results" className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters bar */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="filter-all"
                onClick={() => handleCatClick("all")}
                className={`px-4 py-2 text-xs font-mono-cima uppercase tracking-widest border transition-colors ${
                  activeCat === "all"
                    ? "bg-[var(--cima-navy)] text-white border-[var(--cima-navy)]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[var(--cima-navy)]"
                }`}
              >
                Todos
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`filter-${c.id}`}
                  onClick={() => handleCatClick(c.id)}
                  className={`px-4 py-2 text-xs font-mono-cima uppercase tracking-widest border transition-colors ${
                    activeCat === c.id
                      ? "bg-[var(--cima-red)] text-white border-[var(--cima-red)]"
                      : "bg-white text-slate-700 border-slate-300 hover:border-[var(--cima-red)]"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            {/* Search with autocomplete */}
            <div ref={searchBoxRef} className="relative w-full lg:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                data-testid="catalog-search"
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSuggestOpen(true); setSuggestIndex(-1); }}
                onFocus={() => setSuggestOpen(true)}
                onKeyDown={onSearchKeyDown}
                placeholder='Buscar "MIG", "CGA-870", oxígeno...'
                className="w-full pl-10 pr-9 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[var(--cima-red)]"
                aria-autocomplete="list"
                aria-expanded={suggestOpen && totalSuggestions > 0}
              />
              {search && (
                <button
                  type="button"
                  data-testid="catalog-search-clear"
                  onClick={() => { setSearch(""); setSuggestOpen(false); setSuggestIndex(-1); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {suggestOpen && search.trim().length > 0 && totalSuggestions > 0 && (
                <div
                  data-testid="catalog-suggestions"
                  className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 shadow-xl max-h-[420px] overflow-auto"
                >
                  {suggestions.products.length > 0 && (
                    <div>
                      <div className="px-3 py-2 font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-500 bg-[var(--cima-surface)] border-b border-slate-200">
                        Productos
                      </div>
                      {suggestions.products.map((p, i) => {
                        const active = suggestIndex === i;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`suggest-product-${p.id}`}
                            onMouseEnter={() => setSuggestIndex(i)}
                            onClick={() => selectSuggestion("product", p)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b border-slate-100 last:border-b-0 ${
                              active ? "bg-[var(--cima-red)]/5" : "hover:bg-slate-50"
                            }`}
                          >
                            <div className="w-10 h-10 shrink-0 bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                              {p.imageUrl ? (
                                <img src={p.imageUrl} alt="" className="w-full h-full object-contain p-0.5" loading="lazy" />
                              ) : (
                                <Package className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-semibold text-sm text-[var(--cima-navy)] truncate">{p.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">
                                {CATEGORIES.find((c) => c.id === p.category)?.name} · {p.tag}
                              </div>
                            </div>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {suggestions.tags.length > 0 && (
                    <div>
                      <div className="px-3 py-2 font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-500 bg-[var(--cima-surface)] border-b border-slate-200">
                        Etiquetas / Medidas
                      </div>
                      <div className="flex flex-wrap gap-1.5 p-3">
                        {suggestions.tags.map((t, i) => {
                          const flatIdx = suggestions.products.length + i;
                          const active = suggestIndex === flatIdx;
                          return (
                            <button
                              key={t}
                              type="button"
                              data-testid={`suggest-tag-${t}`}
                              onMouseEnter={() => setSuggestIndex(flatIdx)}
                              onClick={() => selectSuggestion("tag", t)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono-cima uppercase tracking-widest border transition-colors ${
                                active
                                  ? "bg-[var(--cima-red)] text-white border-[var(--cima-red)]"
                                  : "bg-white text-slate-700 border-slate-300 hover:border-[var(--cima-red)]"
                              }`}
                            >
                              <Tag className="w-3 h-3" />
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {suggestions.categories.length > 0 && (
                    <div>
                      <div className="px-3 py-2 font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-500 bg-[var(--cima-surface)] border-b border-slate-200">
                        Categorías
                      </div>
                      {suggestions.categories.map((c, i) => {
                        const flatIdx = suggestions.products.length + suggestions.tags.length + i;
                        const active = suggestIndex === flatIdx;
                        const Icon = ICONS[c.icon] || Package;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            data-testid={`suggest-cat-${c.id}`}
                            onMouseEnter={() => setSuggestIndex(flatIdx)}
                            onClick={() => selectSuggestion("category", c)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b border-slate-100 last:border-b-0 ${
                              active ? "bg-[var(--cima-navy)]/5" : "hover:bg-slate-50"
                            }`}
                          >
                            <div className="w-8 h-8 shrink-0 bg-[var(--cima-navy)] text-white flex items-center justify-center">
                              <Icon className="w-4 h-4" strokeWidth={1.6} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-semibold text-sm text-[var(--cima-navy)]">{c.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">{c.subtitle}</div>
                            </div>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recently viewed strip */}
          {recentProducts.length > 0 && (
            <div data-testid="catalog-recent" className="mb-6 border border-slate-200 bg-white">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-[var(--cima-surface)]">
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-[var(--cima-red)]" />
                  <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-700">
                    Vistos recientemente
                  </span>
                  <span className="text-[10px] font-mono-cima text-slate-400">· {recentProducts.length}</span>
                </div>
                <button
                  type="button"
                  data-testid="catalog-recent-clear"
                  onClick={clearRecent}
                  className="text-[10px] font-mono-cima uppercase tracking-widest text-slate-500 hover:text-[var(--cima-red)] transition-colors"
                >
                  Limpiar
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto p-3 scroll-smooth">
                {recentProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`recent-${p.id}`}
                    onClick={() => openQuote(p.name, p.id)}
                    className="group shrink-0 w-32 flex flex-col items-stretch border border-slate-200 hover:border-[var(--cima-red)] bg-white transition-colors text-left"
                  >
                    <div className="aspect-square bg-white border-b border-slate-100 overflow-hidden flex items-center justify-center">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.name} loading="lazy" className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <Package className="w-8 h-8 text-slate-300" />
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="font-display font-semibold text-[11px] leading-tight text-[var(--cima-navy)] line-clamp-2">
                        {p.name}
                      </div>
                      <div className="text-[9px] font-mono-cima uppercase tracking-widest text-[var(--cima-red)] mt-0.5">
                        Cotizar →
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Medical sub-category bar */}
          {activeCat === "medicinal" && (
            <div data-testid="medical-subfilter" className="flex flex-wrap items-center gap-2 mb-6 p-3 border border-slate-200 bg-[var(--cima-surface)]">
              <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-500 mr-2">
                Sección médica:
              </span>
              <button
                type="button"
                data-testid="medsub-all"
                onClick={() => setActiveSubCat("all")}
                className={`px-3 py-1.5 text-xs font-mono-cima uppercase tracking-widest border transition-colors ${
                  activeSubCat === "all"
                    ? "bg-[var(--cima-navy)] text-white border-[var(--cima-navy)]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[var(--cima-navy)]"
                }`}
              >
                Todos
              </button>
              {MEDICAL_SUBCATEGORIES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`medsub-${s.id}`}
                  onClick={() => setActiveSubCat(s.id)}
                  className={`px-3 py-1.5 text-xs font-mono-cima uppercase tracking-widest border transition-colors ${
                    activeSubCat === s.id
                      ? "bg-[var(--cima-red)] text-white border-[var(--cima-red)]"
                      : "bg-white text-slate-700 border-slate-300 hover:border-[var(--cima-red)]"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          {/* Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-slate-600">
              Mostrando <strong className="text-[var(--cima-navy)]">{filtered.length}</strong>{" "}
              {filtered.length === 1 ? "producto" : "productos"}
              {activeCat !== "all" && (
                <>
                  {" "}en <strong className="text-[var(--cima-red)]">{CATEGORIES.find((c) => c.id === activeCat)?.name}</strong>
                </>
              )}
            </p>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="border border-slate-200 bg-[var(--cima-surface)] p-12 text-center">
              <p className="font-display font-semibold text-lg text-[var(--cima-navy)]">
                Sin resultados
              </p>
              <p className="text-sm text-slate-600 mt-2">
                No encontramos productos que coincidan con tu búsqueda. Intenta con otra palabra.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
              {filtered.map((p) => {
                const Icon = ICONS[p.icon] || Package;
                const hasImage = !!p.imageUrl;
                const isMedical = p.category === "medicinal";
                return (
                  <article
                    key={p.id}
                    data-testid={`catalog-product-${p.id}`}
                    className="sharp-card flex flex-col overflow-hidden bg-white"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-white border-b border-slate-100 flex items-center justify-center">
                      {hasImage ? (
                        <img
                          src={p.imageUrl}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <Icon className="w-16 h-16 text-slate-300" strokeWidth={1.3} />
                      )}
                      <div className="absolute top-3 left-3 bg-[var(--cima-red)] text-white px-2 py-1 text-[10px] font-mono-cima uppercase tracking-widest font-semibold">
                        {p.tag}
                      </div>
                      <div className={`absolute top-3 right-3 px-2 py-1 text-[10px] font-mono-cima uppercase tracking-widest font-semibold ${
                        isMedical ? "bg-[#003DA5] text-white" : "bg-[var(--cima-navy)] text-white"
                      }`}>
                        {CATEGORIES.find((c) => c.id === p.category)?.name}
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1 bg-white">
                      <h3 className="font-display font-semibold text-base lg:text-lg text-[var(--cima-navy)] leading-tight">
                        {p.name}
                      </h3>
                      <p className="text-xs text-slate-600 mt-1.5 leading-relaxed flex-1">{p.desc}</p>

                      {p.sizes && p.sizes.length > 0 && p.sizes[0] !== "Disponible" && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {p.sizes.map((s) => (
                            <span
                              key={s}
                              className={`text-[10px] font-mono-cima uppercase tracking-widest border px-2 py-0.5 ${
                                isMedical
                                  ? "border-[#003DA5]/30 bg-[#003DA5]/5 text-[#003DA5]"
                                  : "border-[var(--cima-red)]/30 bg-[var(--cima-red)]/5 text-[var(--cima-red)]"
                              }`}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        data-testid={`catalog-quote-${p.id}`}
                        onClick={() => openQuote(p.name, p.id)}
                        className={`mt-4 inline-flex items-center justify-between gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                          isMedical
                            ? "bg-[#003DA5] text-white hover:bg-[#002a75]"
                            : "bg-[var(--cima-red)] text-white hover:bg-[var(--cima-red-deep)]"
                        }`}
                      >
                        Cotizar
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 lg:py-20 bg-[var(--cima-navy)] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="divider-orange mb-5" />
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
            ¿No encuentras lo que buscas?
          </h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">
            Nuestro equipo te asesora para encontrar el gas, equipo o consumible que tu
            operación necesita. Pídenos una cotización personalizada.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              data-testid="catalog-cta-quote"
              onClick={() => openQuote("")}
              className="btn-cima-red px-7 py-3.5 text-sm font-semibold uppercase tracking-wide inline-flex items-center justify-center gap-2"
            >
              Solicitar Cotización
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/#contacto"
              data-testid="catalog-cta-contact"
              className="px-7 py-3.5 text-sm font-semibold uppercase tracking-wide border-2 border-white text-white hover:bg-white hover:text-[var(--cima-navy)] transition-colors inline-flex items-center justify-center"
            >
              Contactar
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} defaultProduct={quoteProduct} />
    </div>
  );
}
